import Text from 'components/ui/Text';
import { Fragment, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { LocalCartInput } from 'store/reducers/cartSlice';
import {
    CollectionVariation,
    inputsByUniqueKeyReset,
    inputsByUniqueKeySet,
    selectInputsByUniqueKey,
} from 'store/reducers/productSlice';
import { getProductKey } from './ProductBuy';
import { useScrollableLoading } from 'hooks/useScrollableLoading';
import { TextValidationInput } from './TextValidationInput';
import useAppSelector from 'hooks/useAppSelector';
import useAppDispatch from 'hooks/useAppDispatch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { ThemeContext } from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import Spinner from 'components/ui/Spinner';

type InputMatrixProps = {
    toOrder: LocalCartInput[];
    variations: CollectionVariation[] | undefined;
};

export const InputMatrix = ({ toOrder, variations }: InputMatrixProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const themeContext = useContext(ThemeContext);
    const { visibleItems, containerRef, loadingRef } = useScrollableLoading<HTMLDivElement>(8, 8, [toOrder]);

    const inputsByUniqueKey = useAppSelector(selectInputsByUniqueKey);

    useEffect(() => {
        return () => {
            dispatch(inputsByUniqueKeyReset());
        };
    }, []);

    const allInputItems = toOrder.flatMap((orderItem) => {
        const variation = variations?.find((v) => {
            const skuInVariation = v.skus?.find((s) => s.sku === orderItem.sku)?.sku;
            const orderKey = getProductKey(
                orderItem.variationNumber,
                orderItem.collectionId,
                orderItem.split,
                orderItem.sku,
            );
            const variationKey = getProductKey(v.variationNumber, v.collectionId, v.split, skuInVariation);
            return orderKey === variationKey;
        });

        if (!variation?.inputs || !orderItem.qty) return [];

        return Array.from({ length: orderItem.qty }, (_, index) => {
            return { orderItem, variation, index };
        });
    });

    const visibleInputItems = allInputItems.slice(0, visibleItems);
    const hasMoreToLoad = allInputItems.length > visibleInputItems.length;

    let headers: [string, string][] = [];
    if (variations && variations.length > 0) {
        const uniqueInputsMap = new Map<string, { key: string; name: string }>();
        variations.forEach((variation) => {
            variation.inputs?.forEach((input) => {
                const name = input.name ?? '';
                if (!uniqueInputsMap.has(name)) {
                    uniqueInputsMap.set(name, { key: input.key ?? '', name });
                }
            });
        });

        headers = Array.from(uniqueInputsMap.values()).map((input) => [input.key, input.name]);
    }

    const areAllInputsFilled = (orderItem: LocalCartInput, variation: CollectionVariation, index: number) => {
        if (!variation.inputs || variation.inputs.length === 0) return false;

        const productKey = getProductKey(
            orderItem.variationNumber,
            orderItem.collectionId,
            orderItem.split,
            orderItem.sku,
        );

        return variation.inputs.every((input) => {
            const uniqueInputKey = `${productKey}${index}_${input.key}`;
            return !!inputsByUniqueKey?.[uniqueInputKey]?.input;
        });
    };

    return (
        <>
            {!!visibleInputItems?.length && (
                <>
                    <InputMatrixWrapper ref={containerRef} className="mb-3">
                        <Matrix>
                            <MatrixBody>
                                <Row>
                                    <HeaderCellRow
                                        single={!!headers && headers.length === 1}
                                        as="th"
                                        className="f3-700"
                                    >
                                        <Text fontSize={12} lineheight={12} className={'mt-1 mx-3'}>
                                            {t('article_one')}
                                        </Text>
                                    </HeaderCellRow>
                                    {headers &&
                                        headers.map(([key, val]) => (
                                            <HeaderCellCol as="th" key={key}>
                                                <Text small className="text-capitalize">
                                                    {val}
                                                </Text>
                                            </HeaderCellCol>
                                        ))}
                                </Row>

                                {visibleInputItems.map(({ orderItem, variation, index }) => {
                                    const inputInstanceKey = `text_validation_input_${orderItem.sku}_${index}`;

                                    const skuDetails = variation.skus?.find((sku) => sku.sku === orderItem.sku);
                                    const displayName = `${variation.color ?? variation.variationNumber} / ${
                                        skuDetails?.name ?? ''
                                    }`;

                                    return (
                                        <Fragment key={inputInstanceKey}>
                                            <Row>
                                                <HeaderCellRow className="f3-700">
                                                    <Text
                                                        fontSize={!!variation.split ? 11 : 12}
                                                        lineheight={12}
                                                        light={true}
                                                        className={'mt-1 mx-3'}
                                                    >
                                                        {displayName}
                                                        {areAllInputsFilled(orderItem, variation, index) && (
                                                            <span className="mx-3">
                                                                <FontAwesomeIcon
                                                                    size={'lg'}
                                                                    color={themeContext.colors.success}
                                                                    icon={faCheck}
                                                                />
                                                            </span>
                                                        )}
                                                    </Text>
                                                </HeaderCellRow>
                                                {headers &&
                                                    headers.map(([headerKey, headerVal]) => {
                                                        // Find if this variation has an input matching the current header name
                                                        const matchingInput = variation.inputs?.find(
                                                            (input) => (input.name ?? '') === headerVal,
                                                        );

                                                        const uniqueInputKey = matchingInput
                                                            ? `${getProductKey(
                                                                  orderItem.variationNumber,
                                                                  orderItem.collectionId,
                                                                  orderItem.split,
                                                                  orderItem.sku,
                                                              )}${index}_${matchingInput.key}`
                                                            : '';

                                                        return (
                                                            <Cell key={headerVal} className="mb-2">
                                                                {matchingInput ? (
                                                                    <StyledTextValidationInput
                                                                        value={
                                                                            inputsByUniqueKey?.[uniqueInputKey]
                                                                                ?.input ?? ''
                                                                        }
                                                                        validation={matchingInput.validation}
                                                                        maxChars={matchingInput.maxChars}
                                                                        required
                                                                        onChange={(e) => {
                                                                            dispatch(
                                                                                inputsByUniqueKeySet({
                                                                                    key: uniqueInputKey,
                                                                                    inputs: {
                                                                                        input: e,
                                                                                        key: matchingInput.key,
                                                                                    },
                                                                                }),
                                                                            );
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div></div>
                                                                )}
                                                            </Cell>
                                                        );
                                                    })}
                                            </Row>
                                        </Fragment>
                                    );
                                })}
                            </MatrixBody>
                        </Matrix>
                        {hasMoreToLoad && (
                            <div ref={loadingRef}>
                                <Spinner />
                            </div>
                        )}
                    </InputMatrixWrapper>
                </>
            )}
        </>
    );
};

const InputMatrixWrapper = styled.div`
    overflow: auto;
    max-height: 300px;
`;

const Matrix = styled.table<{ fixed?: boolean }>`
    width: 100%;
    table-layout: fixed;
    border-spacing: 2px;
    border-collapse: separate;
    margin-top: -2px;
`;

const MatrixBody = styled.tbody`
    background: ${({ theme }) => theme.colors.fillMedium};
`;

const Row = styled.tr`
    &:last-of-type {
        border-bottom: none;
    }
`;
const Cell = styled.td`
    position: relative;
    width: 80px;
    height: 42px;

    &:last-of-type {
        border-right: none;
    }
`;

const HeaderCellRow = styled(Cell)<{ single?: boolean }>`
    width: ${({ single }) => (single ? '50%' : '138px')};
`;

const HeaderCellCol = styled(Cell)`
    text-align: center;
`;

const StyledTextValidationInput = styled(TextValidationInput)`
    width: 100%;
    border: 1px solid transparent;
    padding: 0.2rem 0.45rem;
    text-align: center;

    && input {
        text-align: center;
    }
`;
